'use client';

import type { FC } from 'react';
import { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { ScreenContent, ScreenFixedTop, ScreenTabBar } from '../../screen';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Button, EmptyState } from '../../components/ui';
import { GiOpenChest } from '../../components/icons';
import { CaseCard } from '../../components/cases/CaseCard';
import { CaseCardSkeleton } from '../../components/CaseCardSkeleton';
import { CaseCatalog } from '../../components/cases/catalog/CaseCatalog';
import type { CaseCatalog as CaseCatalogData, CatalogCase, CatalogSort } from '../../components/cases/catalog/catalog.types';
import { ALL_CATEGORIES, readStoredCatalogSort, storeCatalogSort } from '../../components/cases/catalog/catalogSort';
import { useNavigation } from '../../contexts/NavigationContext';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useCasesCatalogQuery } from '../../queries/useCasesQuery';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import translations from './CasesScreen.translations';

const GRID_MOBILE = 'grid grid-cols-[repeat(auto-fill,minmax(min(100%,150px),1fr))] gap-2';
const GRID_DESKTOP = 'grid grid-cols-4 gap-3';

interface CasesScreenProps {
  params?: { category?: string };
  catalog?: CaseCatalogData;
  initialSort?: CatalogSort;
}

const readCategoryFromLocation = (): string | null => {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('category');
};

export const CasesScreen: FC<CasesScreenProps> = ({ params, catalog: catalogOverride, initialSort }) => {
  const { t } = useLocalTranslation(translations);
  const navigation = useNavigation();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error, refetch } = useCasesCatalogQuery();
  const isDesktop = useIsDesktop();

  const catalog = useMemo<CaseCatalogData>(() => catalogOverride ?? data ?? { general: [], categories: [] }, [catalogOverride, data]);
  const isEmpty = catalog.general.length === 0;
  const showSkeleton = isLoading && !catalogOverride && isEmpty;

  const [sort, setSort] = useState<CatalogSort>(() => initialSort ?? readStoredCatalogSort());
  const handleSortChange = useCallback((next: CatalogSort) => {
    setSort(next);
    storeCatalogSort(next);
  }, []);

  const paramsCategory = params?.category;
  const [category, setCategory] = useState<string>(paramsCategory ?? ALL_CATEGORIES);
  useEffect(() => {
    const next = paramsCategory ?? readCategoryFromLocation();
    if (next && next !== category) setCategory(next);
  }, [paramsCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCategoryChange = useCallback(
    (slug: string) => {
      setCategory(slug);
      navigation.updateCurrentParams({ category: slug === ALL_CATEGORIES ? undefined : slug });
    },
    [navigation],
  );

  const handleCaseClick = useCallback(
    (caseId: string) => {
      navigation.navigate('case-details', { caseId });
    },
    [navigation],
  );

  const renderCase = useCallback(
    (caseItem: CatalogCase) => (
      <CaseCard
        key={caseItem.id}
        id={caseItem.id}
        name={caseItem.name}
        imageUrl={caseItem.imageUrl}
        imagePlaceholder={caseItem.imagePlaceholder}
        priceInCredits={caseItem.priceInCredits}
        openingsCount={caseItem.openingsCount}
        isNew={caseItem.isNew}
        isPopular={caseItem.isPopular}
        onClick={handleCaseClick}
      />
    ),
    [handleCaseClick],
  );

  if (showSkeleton) {
    return (
      <>
        <ScreenFixedTop title={t('title')} />
        <ScreenTabBar />
        <ScreenContent scrollRef={scrollRef} scrollKey="cases">
          <div className={`px-4 pt-4 ${isDesktop ? GRID_DESKTOP : GRID_MOBILE}`}>
            {[...Array(6)].map((_, index) => (
              <CaseCardSkeleton key={`skeleton-${index}`} />
            ))}
          </div>
        </ScreenContent>
      </>
    );
  }

  if (error && isEmpty) {
    return (
      <>
        <ScreenFixedTop title={t('title')} />
        <ScreenTabBar />
        <ScreenContent>
          <ErrorMessage onRetry={() => refetch()} />
        </ScreenContent>
      </>
    );
  }

  return (
    <>
      <ScreenFixedTop title={t('title')} />
      <ScreenTabBar />
      <ScreenContent scrollRef={scrollRef} scrollKey="cases">
        {isEmpty ? (
          <EmptyState
            icon={<GiOpenChest size={48} className="text-white/20" />}
            title={t('empty')}
            description={t('emptyHint')}
            action={
              <Button size="l" onClick={() => navigation.navigate('games')}>
                {t('emptyCta')}
              </Button>
            }
          />
        ) : (
          <div className="px-4 pt-4">
            <CaseCatalog
              catalog={catalog}
              sort={sort}
              onSortChange={handleSortChange}
              category={category}
              onCategoryChange={handleCategoryChange}
              gridClassName={isDesktop ? GRID_DESKTOP : GRID_MOBILE}
              renderCase={renderCase}
            />
          </div>
        )}
      </ScreenContent>
    </>
  );
};
